import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { cocktailApi, Cocktail } from '../services/cocktailApi.ts';
import CocktailCard from '../components/CocktailCard.tsx';
import { validateSearchQuery } from '../utils/validation.ts';
import './Search.css';

const Search: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const [cocktails, setCocktails] = useState<Cocktail[]>([]);
  const [loading, setLoading] = useState(false);

  const performSearch = useCallback(async (searchQuery: string) => {
    setLoading(true);
    let results: Cocktail[] = [];

    try {
      // Search both by name and ingredient simultaneously
      const [nameResults, ingredientResults] = await Promise.all([
        cocktailApi.searchCocktails(searchQuery),
        cocktailApi.getCocktailsByIngredient(searchQuery)
      ]);

      // Ensure results are arrays
      const nameResultsArray = Array.isArray(nameResults) ? nameResults : [];
      const ingredientResultsArray = Array.isArray(ingredientResults) ? ingredientResults : [];

      // Merge results and remove duplicates by idDrink
      const cocktailsMap = new Map<string, Cocktail>();
      
      // Add name results first
      nameResultsArray.forEach(cocktail => {
        if (cocktail.idDrink) {
          cocktailsMap.set(cocktail.idDrink, cocktail);
        }
      });
      
      // Add ingredient results (will overwrite if duplicate, but that's fine)
      ingredientResultsArray.forEach(cocktail => {
        if (cocktail.idDrink) {
          cocktailsMap.set(cocktail.idDrink, cocktail);
        }
      });

      // Convert to array and sort alphabetically by drink name
      results = Array.from(cocktailsMap.values()).sort((a, b) => {
        const nameA = (a.strDrink || '').toLowerCase();
        const nameB = (b.strDrink || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
    } catch (error) {
      console.error('Search error:', error);
      results = [];
    }

    setCocktails(results);
    setLoading(false);
  }, []);

  // Initialize state from URL parameters
  useEffect(() => {
    const urlQuery = validateSearchQuery(searchParams.get('q'));

    if (urlQuery) {
      setQuery(urlQuery);
      // Auto-search if we have a validated query from URL
      performSearch(urlQuery);
    }
  }, [searchParams, performSearch]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const validatedQuery = validateSearchQuery(query);
    if (!validatedQuery) {
      return;
    }

    setQuery(validatedQuery);
    performSearch(validatedQuery);
  };

  return (
    <div className="search">
      <h1>Search Cocktails</h1>

      <form onSubmit={handleSearch} className="search-form">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by drink name or ingredient..."
          className="search-input"
        />
        <button type="submit" className="search-btn" disabled={loading}>
          {loading ? 'Searching...' : 'Search'}
        </button>
      </form>

      {Array.isArray(cocktails) && cocktails.length > 0 && (
        <div className="search-results">
          <h2>Search Results ({cocktails.length})</h2>
          <div className="cocktails-grid">
            {cocktails.map(cocktail => (
              <CocktailCard key={cocktail.idDrink} cocktail={cocktail} hideCategoryAndAlcoholic={true} />
            ))}
          </div>
        </div>
      )}

      {query && cocktails.length === 0 && !loading && (
        <p className="no-results">No cocktails found for "{query}"</p>
      )}
    </div>
  );
};

export default Search;
